import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { useScrollEnd } from "@/hooks/use-scroll-end";
import {
    Combobox as AriakitCombobox,
    ComboboxItem as AriakitComboboxItem,
    ComboboxList as AriakitComboboxList,
    ComboboxProvider as AriakitComboboxProvider,
    Select as AriakitSelect,
    SelectArrow as AriakitSelectArrow,
    SelectItem as AriakitSelectItem,
    SelectItemCheck as AriakitSelectItemCheck,
    SelectPopover as AriakitSelectPopover,
    SelectProvider as AriakitSelectProvider,
    useComboboxContext as useAriakitComboboxContext,
    useComboboxStore as useAriakitComboboxStore,
    useSelectContext as useAriakitSelectContext,
} from "@ariakit/react";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import IconMagnifyingGlass from "central-icons/IconMagnifyingGlass";
import { matchSorter } from "match-sorter";
import type React from "react";
import {
    Children,
    createContext,
    isValidElement,
    useContext,
    useEffect,
    useMemo,
    type FC,
    type PropsWithChildren,
} from "react";

/**
 * Component displayName constants for Fast Refresh compatibility.
 * Using displayName for component identification ensures components are recognized
 * correctly even after Fast Refresh updates, as displayName strings remain stable
 * while function references may change.
 */
const COMBOBOX_VALUE = "Combobox.Value";
const COMBOBOX_LIST = "Combobox.List";
const COMBOBOX_POPOVER = "Combobox.Popover";
const COMBOBOX_LOADING = "Combobox.Loading";
const COMBOBOX_EMPTY = "Combobox.Empty";
const COMBOBOX_SEARCH_BOX = "Combobox.SearchBox";
const COMBOBOX_LIST_ITEM_LEADING_ICON = "Combobox.ListItemLeadingIcon";
const COMBOBOX_LIST_ITEM_TITLE = "Combobox.ListItemTitle";
const COMBOBOX_LIST_ITEM_DESCRIPTION = "Combobox.ListItemDescription";
const COMBOBOX_FOOTER = "Combobox.Footer";

/**
 * Props for the Combobox.List component.
 *
 * @template T - The type of items in the list
 */
export type ComboboxListProps<T> = Omit<React.ComponentProps<typeof AriakitComboboxList>, "children"> & {
    /** Array of items to display */
    items: T[];
    /** Keys to search on to filter the list. Supports dot notation for nested keys. */
    searchKeys: string[];
    /** Render function for each item */
    children: (item: T) => React.ReactNode;
    /** Custom component to show when no results are found */
    noMatchesComponent?: React.ReactNode;
    /** Disable filtering */
    disableFiltering?: boolean;
    /** Callback function that gets triggered when the user scrolls to the end of the list */
    onScrollEnd?: () => void;
    /** Threshold in pixels from the bottom to trigger onScrollEnd (default: 100) */
    scrollEndThreshold?: number;
};

/**
 * Props for the root Combobox component.
 *
 * The root component of the combobox. Any sub-components must be rendered as children of this component.
 */
export type ComboboxProps = PropsWithClassName & {
    /**
     * The default value of the combobox for uncontrolled usage.
     */
    defaultValue?: string;

    /**
     * The current value of the combobox.
     * Use in combination with `onValueChange` to make the combobox controlled.
     */
    value?: string;

    /**
     * A callback function that is called with the selected value when the value of the combobox changes.
     */
    onValueChange?: (value: string) => void;

    /**
     * The placeholder text for the search box in the combobox.
     * Defaults to "Search" if unspecified.
     */
    filterPlaceholder?: string;

    /**
     * Whether the combobox is in loading state. Default is false.
     * If true, the combobox will be disabled and a loading spinner will be displayed.
     *
     * The label of the dropdown will be replaced with the loading label,
     * which is inferred from Combobox.Loading if it is provided as a child.
     * Otherwise, it will default to "Loading…".
     */
    loading?: boolean;

    /**
     * Whether the combobox is disabled. Default is false.
     * If true, the combobox will be disabled and the dropdown will not be openable.
     */
    disabled?: boolean;
};

/**
 * Available slot components for the Combobox.
 */
export type ComboboxSlots = {
    /**
     * Renders a popover for the combobox with a search box automatically inserted.
     * If not provided, all the components that belong in the popover will be automatically wrapped in this component.
     * If you want to customize the popover, for example to override the default width of the popover, pass this component as a child to the Combobox component,
     * then put all the components that belong in the popover in this component.
     *
     * By default, the popover will be the same width as the trigger.
     *
     * **Slots:**
     * - Combobox.List
     * - Combobox.Empty
     * - Combobox.Loading
     */
    Popover: typeof ComboboxPopover;

    /**
     * Customizes how the selected value is displayed using either a render function or a react node.
     * It renders `children` in the dropdown trigger. You must wrap whatever you want to render in the trigger in this component.
     *
     * If rendered as is without any child, the selected value will be displayed using ValueLabel.
     */
    Value: typeof ComboboxValue;

    /**
     * Default styled label for the selected value.
     * Usually rendered as a child of Value.
     */
    ValueLabel: typeof ComboboxValueLabel;

    /**
     * Renders a list of selectable items with filtering.
     */
    List: typeof ComboboxList;

    /**
     * Individual item in the list. When selected, a checkmark icon will be displayed at the end (right hand side) of the item.
     */
    ListItem: typeof ComboboxListItem;

    /**
     * Styled title text for the list item.
     */
    ListItemTitle: typeof ComboboxListItemTitle;

    /**
     * Styled description text for list items.
     */
    ListItemDescription: typeof ComboboxListItemDescription;

    /**
     * Container for an icon at the start of a list item.
     */
    ListItemLeadingIcon: typeof ComboboxListItemLeadingIcon;

    /**
     * Styled empty label for the combobox that can be used to display a message when there are no options to display.
     *
     * Usually:
     * - passed as a child to either Combobox or Combobox.Popover when there are no options to display; or
     * - passed as the `noMatchesComponent` prop to the Combobox.List component when the options are all filtered out.
     *
     * You can have both in conjunction!
     */
    Empty: typeof ComboboxEmpty;

    /**
     * Styled loading label for the combobox that can be used to display a message when the combobox is loading.
     * Usually passed as a child to the Combobox component.
     */
    Loading: typeof ComboboxLoading;

    /**
     * Search input for filtering the combobox options.
     * Can be controlled via value/onValueChange props or used uncontrolled.
     * Shows a loading spinner when loading prop is true.
     *
     * Should be placed inside Combobox.Popover or directly under Combobox.
     */
    SearchBox: typeof ComboboxSearchBox;

    /**
     * Footer for the combobox.
     */
    Footer: typeof ComboboxFooter;
};

/**
 * Props for the Combobox.Popover component.
 */
export type ComboboxPopoverProps = Omit<React.ComponentProps<typeof AriakitSelectPopover>, "sameWidth"> & {
    /** The placeholder text for the search box in the popover */
    filterPlaceholder?: string;
    /**
     * Whether the popover should match the trigger width. Defaults to true.
     * When false, you can use min-w and max-w classes to control the width.
     */
    sameWidth?: boolean;
    /**
     * Controls the behavior of the popover when it overflows the viewport.
     * - If a boolean, specifies whether the popover should flip to the opposite side when it overflows.
     * - If a string, indicates the preferred fallback placements when it overflows.
     * @default true
     */
    flip?: boolean | string;
    /**
     * Whether the popover should slide when it overflows.
     * @default true
     */
    slide?: boolean;
    /**
     * Whether the popover should fit the viewport. If true, the popover will have
     * maxWidth and maxHeight set to the viewport size.
     * @default false
     */
    fitViewport?: boolean;
};

/**
 * Props for the Combobox.ListItem component.
 */
export type ComboboxListItemProps = React.ComponentProps<typeof ComboboxListItem> & {
    /** Shorthand for simple text items */
    title?: React.ReactNode;
};

/**
 * Props for the Combobox.ListItemLeadingIcon component.
 */
export type ComboboxListItemLeadingIconProps = React.ComponentProps<"span">;

/**
 * Props for the Combobox.Empty component.
 */
export type ComboboxEmptyProps = React.ComponentProps<typeof Text>;

/**
 * Props for the Combobox.Loading component.
 */
export type ComboboxLoadingProps = React.ComponentProps<typeof Text>;

const ComboboxContext = createContext<{
    filterPlaceholder: string;
} | null>(null);

const useComboboxContext = (componentName: string) => {
    const context = useContext(ComboboxContext);
    if (!context) {
        throw new Error(`${componentName} must be used within a Combobox`);
    }
    return context;
};

/**
 * Helper function to get the displayName from a React element.
 * Used for component type identification that survives Fast Refresh.
 */
const getComponentDisplayName = (element: React.ReactElement): string | undefined => {
    const type = element.type as React.ComponentType;
    return type.displayName;
};

/**
 * A searchable select component that combines a text input with a dropdown list.
 *
 * ## Features
 * - **Searchable**: Filter options by typing in the input field
 * - **Keyboard Navigation**: Full keyboard support for accessibility
 * - **Customizable**: Flexible composition with sub-components
 * - **Empty State**: Configurable empty state when no results are found
 * - **Controlled/Uncontrolled**: Supports both controlled and uncontrolled modes
 *
 * @example
 * ```tsx
 * <Combobox defaultValue="Apple">
 *   <Combobox.List
 *     items={items}
 *     searchKeys={["name", "category"]}
 *     noMatchesComponent={<Combobox.Empty>No fruits found</Combobox.Empty>}
 *   >
 *     {(item) => <Combobox.ListItem key={item.id} value={item.name} title={item.name} />}
 *   </Combobox.List>
 * </Combobox>
 * ```
 */
const Combobox: FC<PropsWithChildren<ComboboxProps>> & ComboboxSlots = ({
    children,
    defaultValue,
    value,
    onValueChange,
    filterPlaceholder = "Search",
    loading = false,
    disabled = false,
    className,
}) => {
    const store = useAriakitComboboxStore();

    let selectedValue: React.ReactNode | null = null;
    let optionList: React.ReactNode | null = null;
    let popover: React.ReactNode | null = null;
    let loadingComponent: React.ReactNode | null = null;
    let loadingLabel: React.ReactNode | null = null;
    let empty: React.ReactNode | null = null;
    let footer: React.ReactNode | null = null;

    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const displayName = getComponentDisplayName(child);

            switch (displayName) {
                case COMBOBOX_VALUE:
                    selectedValue = child;
                    break;
                case COMBOBOX_LIST:
                    optionList = child;
                    break;
                case COMBOBOX_LOADING:
                    loadingComponent = child;
                    loadingLabel = child.props.children as React.ReactNode;
                    if (!loadingLabel) {
                        loadingLabel = "Loading…";
                    }
                    break;
                case COMBOBOX_POPOVER:
                    popover = child;
                    // Combobox.Loading can either be a direct child of Combobox root or a child of Combobox.Popover.
                    // Since we found a Combobox.Popover, we need to look for a Combobox.Loading child within it.
                    // and then determine the loading label from it.
                    Children.forEach(child.props.children as React.ReactNode, (nestedChild: React.ReactNode) => {
                        if (isValidElement(nestedChild) && getComponentDisplayName(nestedChild) === COMBOBOX_LOADING) {
                            loadingLabel = nestedChild.props.children as React.ReactNode;
                            if (!loadingLabel) {
                                loadingLabel = "Loading…";
                            }
                        }
                    });
                    break;
                case COMBOBOX_EMPTY:
                    empty = child;
                    break;
                case COMBOBOX_FOOTER:
                    footer = child;
                    break;
                default:
                    break;
            }
        }
    });

    if (!selectedValue) {
        selectedValue = <ComboboxValue />;
    }

    if (!popover) {
        popover = (
            <ComboboxPopover>
                <ComboboxSearchBox placeholder={filterPlaceholder} />
                {loadingComponent}
                {empty}
                {optionList}
                {footer}
            </ComboboxPopover>
        );
    }

    return (
        <ComboboxContext.Provider
            value={{
                filterPlaceholder,
            }}
        >
            <AriakitComboboxProvider store={store}>
                <AriakitSelectProvider
                    defaultValue={defaultValue}
                    value={value}
                    setValue={onValueChange}
                    setOpen={(open) => {
                        if (!open) {
                            store.setValue("");
                        }
                    }}
                >
                    <AriakitSelect
                        className={cn(
                            "flex h-9 min-w-40 items-center justify-between gap-2 rounded-lg border border-border-input-default bg-surface-input px-3",
                            "transition-all duration-150 ease-out",
                            "outline-none",
                            // Focus state (tab navigation): brand ring
                            "focus:border-border-input-active focus:ring-1 focus:ring-border-brand focus:ring-offset-1",
                            // Expanded state (clicked/open): input active state
                            "aria-expanded:border-border-input-active aria-expanded:ring-4 aria-expanded:ring-ring-default aria-expanded:ring-offset-0",
                            {
                                "cursor-not-allowed opacity-50": loading,
                            },
                            className,
                        )}
                        disabled={loading || disabled}
                    >
                        {loading ? <ComboboxValueLabel>{loadingLabel}</ComboboxValueLabel> : selectedValue}
                        {loading ? (
                            <IconSpinner className="animate-spin text-content-primary" size="sm" />
                        ) : (
                            <AriakitSelectArrow
                                style={{ width: 20, height: 20 }}
                                render={<IconChevronDownSmall size={20} />}
                            />
                        )}
                    </AriakitSelect>
                    {popover}
                </AriakitSelectProvider>
            </AriakitComboboxProvider>
        </ComboboxContext.Provider>
    );
};

/**
 * Renders a popover for the combobox with a search box automatically inserted.
 *
 * If not provided, all the components that belong in the popover will be automatically wrapped in this component.
 * If you want to customize the popover (e.g., to override the default width), pass this component as a child
 * to the Combobox component, then put all the components that belong in the popover in this component.
 *
 * By default, the popover will be the same width as the trigger.
 */
const ComboboxPopover: FC<ComboboxPopoverProps> = ({
    children,
    className,
    sameWidth = true,
    flip = true,
    slide = true,
    fitViewport = true,
    ...props
}) => {
    const { filterPlaceholder } = useComboboxContext("ComboboxPopover");

    let searchBox: React.ReactNode | null = null;
    let isSearchBoxFoundInChildren = false;
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            if (getComponentDisplayName(child) === COMBOBOX_SEARCH_BOX) {
                searchBox = child;
                isSearchBoxFoundInChildren = true;
            }
        }
    });
    if (!searchBox) {
        searchBox = <ComboboxSearchBox placeholder={filterPlaceholder} />;
    }
    return (
        <AriakitSelectPopover
            className={cn(
                // z-index matches Select component for consistent layering above content
                "relative z-[999]",
                sameWidth && "w-[--popover-anchor-width]",
                "overflow-auto rounded-lg border border-border-base bg-surface-popover shadow",
                // Smooth Tailwind UI-style transitions
                "origin-top transition-all duration-200 ease-out",
                "data-[open]:scale-100 data-[open]:opacity-100",
                "data-[closed]:scale-95 data-[closed]:opacity-0",
                className,
            )}
            gutter={4}
            sameWidth={sameWidth}
            flip={flip}
            slide={slide}
            fitViewport={fitViewport}
            modal={false}
            {...props}
        >
            {isSearchBoxFoundInChildren ? null : searchBox}
            {children}
        </AriakitSelectPopover>
    );
};
ComboboxPopover.displayName = COMBOBOX_POPOVER;

/**
 * Props for the Combobox.SearchBox component.
 */
export type ComboboxSearchBoxProps = Omit<React.ComponentProps<typeof AriakitCombobox>, "value" | "onChange"> & {
    /**
     * Callback when the internal search value has changed.
     */
    onValueChanged?: (value: string) => void;

    /**
     * Whether the search is in a loading state.
     * If true, a loading spinner will be displayed.
     */
    loading?: boolean;
};

/**
 * Search input for the combobox with optional loading spinner.
 * If `onValueChanged` is provided, the search value will be watched and passed to the callback when it changes.
 * If `loading` is true, a loading spinner will be displayed to the right of the search box.
 */
const ComboboxSearchBox: FC<ComboboxSearchBoxProps> = ({
    children,
    className,
    onValueChanged,
    loading = false,
    ...props
}) => {
    const { filterPlaceholder } = useComboboxContext("ComboboxSearchBox");

    // the translations are there for optical alignment
    return (
        <ComboboxSearchValueWatcher onValueChanged={onValueChanged}>
            <div className="relative flex h-9 w-full items-center gap-2 border-b border-border-base px-2.5">
                <IconMagnifyingGlass className="-translate-y-[0.5px] text-content-muted" size={20} />
                <AriakitCombobox
                    placeholder={filterPlaceholder}
                    className={cn(
                        "h-9 w-full max-w-full -translate-y-px bg-transparent py-3 text-base text-content-primary placeholder:translate-y-px placeholder:text-base placeholder:text-content-muted focus:outline-none focus:ring-0",
                        loading ? "pr-8" : "",
                        className,
                    )}
                    autoSelect="always"
                    autoComplete="list"
                    autoFocus
                    {...props}
                >
                    {children}
                </AriakitCombobox>
                {loading && <IconSpinner className="absolute right-2.5 animate-spin text-content-muted" size="sm" />}
            </div>
        </ComboboxSearchValueWatcher>
    );
};
ComboboxSearchBox.displayName = COMBOBOX_SEARCH_BOX;

/**
 * Watches the internal search value of the combobox and passes it to the `onValueChanged` callback when it changes.
 * Not the nicest but Ariakit does not provide a way to watch the internal value of the combobox outside of the component lifecycle. This avoids unnecessary re-renders.
 */
const ComboboxSearchValueWatcher: FC<PropsWithChildren<{ onValueChanged?: (value: string) => void }>> = ({
    children,
    onValueChanged,
}) => {
    const comboboxContext = useAriakitComboboxContext();
    if (!comboboxContext) {
        throw new Error("ComboboxSearchBox must be used within a Combobox");
    }
    const value = comboboxContext.useState("value");
    useEffect(() => {
        onValueChanged?.(value);
    }, [value, onValueChanged]);
    return children;
};

/**
 * Renders a list of selectable items with filtering.
 *
 * Accepts a render function that will be called for each item in the list and should return
 * the component to render for that item.
 *
 * @example
 * ```tsx
 * <Combobox.List
 *   items={items}
 *   searchKeys={["name", "category"]}
 *   noMatchesComponent={<Combobox.Empty>No items found</Combobox.Empty>}
 * >
 *   {(item) => <Combobox.ListItem key={item.id} value={item.name} title={item.name} />}
 * </Combobox.List>
 * ```
 */
const ComboboxList = <T,>({
    items,
    searchKeys,
    children,
    noMatchesComponent,
    className,
    onScrollEnd,
    scrollEndThreshold = 100,
    disableFiltering = false,
    ...props
}: ComboboxListProps<T>) => {
    const comboboxContext = useAriakitComboboxContext();
    if (!comboboxContext) {
        throw new Error("ComboboxList must be used within a Combobox");
    }

    const searchValue = comboboxContext.useState("value");

    const filtered = useMemo(() => {
        if (!searchValue || disableFiltering) {
            return items;
        }
        const terms = searchValue.split(" ");
        return terms.reduceRight((results, term) => matchSorter(results, term, { keys: searchKeys }), items);
    }, [disableFiltering, items, searchKeys, searchValue]);

    let content: React.ReactNode;
    let areAllFiltered = false;
    // if there are matches, render the matches
    // if there is a search value and no matches, render the empty component
    // if there is no search value and no matches, render all items
    if (filtered.length > 0) {
        content = filtered.map(children);
    } else if (searchValue) {
        content = noMatchesComponent || <ComboboxEmpty />;
        areAllFiltered = true;
    } else {
        content = items.map(children);
    }

    const { scrollContainerRef, onScroll: handleScroll } = useScrollEnd({
        onScrollEnd,
        threshold: scrollEndThreshold,
    });

    return (
        <AriakitComboboxList
            ref={scrollContainerRef}
            className={cn("max-h-[320px] overflow-auto", areAllFiltered ? "p-0" : "p-1", className)}
            onScroll={handleScroll}
            {...props}
        >
            {content}
        </AriakitComboboxList>
    );
};
ComboboxList.displayName = COMBOBOX_LIST;

/**
 * Individual item in the list. When selected, a checkmark icon will be displayed at the end (right hand side) of the item.
 *
 * @example
 * ```tsx
 * <Combobox.ListItem value="apple" title="Apple" />
 * ```
 *
 * @example With custom content
 * ```tsx
 * <Combobox.ListItem value="apple">
 *   <Combobox.ListItemLeadingIcon><IconStar /></Combobox.ListItemLeadingIcon>
 *   <Combobox.ListItemTitle>Apple</Combobox.ListItemTitle>
 *   <Combobox.ListItemDescription>Fruit</Combobox.ListItemDescription>
 * </Combobox.ListItem>
 * ```
 */
const ComboboxListItem: FC<React.ComponentProps<typeof AriakitSelectItem>> = ({
    children,
    className,
    title,
    ...props
}) => {
    let content: React.ReactNode | null = null;
    if (children) {
        let leadingIcon: React.ReactNode | null = null;
        let title: React.ReactNode | null = null;
        let description: React.ReactNode | null = null;
        Children.forEach(children, (child) => {
            if (isValidElement(child)) {
                const displayName = getComponentDisplayName(child);
                switch (displayName) {
                    case COMBOBOX_LIST_ITEM_LEADING_ICON:
                        // Only set leadingIcon if it has children
                        if (child.props.children) {
                            leadingIcon = child;
                        }
                        break;
                    case COMBOBOX_LIST_ITEM_TITLE:
                        title = child;
                        break;
                    case COMBOBOX_LIST_ITEM_DESCRIPTION:
                        description = child;
                        break;
                }
            }
        });
        if (title && description) {
            content = (
                <div className="flex items-center">
                    {leadingIcon}
                    <div className="flex flex-col">
                        {title}
                        {description}
                    </div>
                </div>
            );
        } else if (leadingIcon && title) {
            content = (
                <div className="flex min-w-0 items-center gap-1.5">
                    {leadingIcon}
                    <div className="min-w-0 flex-1">{title}</div>
                </div>
            );
        } else {
            content = children;
        }
    } else if (title && typeof title === "string") {
        content = <ComboboxListItemTitle>{title}</ComboboxListItemTitle>;
    }

    // the spacing between items is applied by adding a bottom padding to each item except the last item
    // this ensures that the item stays highlighted when hovering over the gaps between items
    // if the gap is applied from the parent List using space-y-1, then hovering over the gap will not highlight the item

    return (
        <AriakitSelectItem
            className={cn(
                "group/combobox-item flex cursor-pointer select-none items-center justify-between pb-1 text-base text-content-primary first:pt-0 last:pb-0",
                className,
            )}
            render={<AriakitComboboxItem />}
            {...props}
        >
            <div className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 group-hover/combobox-item:bg-surface-hover group-data-[active-item]/combobox-item:bg-surface-hover">
                <div className="min-w-0 flex-1">{content}</div>
                <AriakitSelectItemCheck className="flex-none">
                    <IconCheckmark2Small className="size-4" />
                </AriakitSelectItemCheck>
            </div>
        </AriakitSelectItem>
    );
};
ComboboxListItem.displayName = "Combobox.ListItem";

/**
 * Styled title text for the list item.
 */
const ComboboxListItemTitle: FC<React.ComponentProps<typeof Text>> = ({ children, className, ...props }) => {
    return (
        <Text className={cn("truncate text-content-primary", className)} {...props}>
            {children}
        </Text>
    );
};
ComboboxListItemTitle.displayName = COMBOBOX_LIST_ITEM_TITLE;

/**
 * Styled description text for list items.
 */
const ComboboxListItemDescription: FC<React.ComponentProps<typeof Text>> = ({ children, className, ...props }) => {
    return (
        <Text className={cn("text-sm text-content-secondary", className)} {...props}>
            {children}
        </Text>
    );
};
ComboboxListItemDescription.displayName = COMBOBOX_LIST_ITEM_DESCRIPTION;

/**
 * Container for an icon at the start of a list item.
 */
const ComboboxListItemLeadingIcon: FC<ComboboxListItemLeadingIconProps> = ({ children, className, ...props }) => {
    if (!children) return null;

    return (
        <span className={cn("mr-2", className)} {...props}>
            {children}
        </span>
    );
};
ComboboxListItemLeadingIcon.displayName = COMBOBOX_LIST_ITEM_LEADING_ICON;

/**
 * Styled empty label for the combobox that can be used to display a message when there are no options to display.
 *
 * Usually:
 * - passed as a child to either Combobox or Combobox.Popover when there are no options to display; or
 * - passed as the `noMatchesComponent` prop to the Combobox.List component when the options are all filtered out.
 *
 * You can have both in conjunction!
 *
 * @example No options available
 * ```tsx
 * <Combobox>
 *   <Combobox.Empty>No options available</Combobox.Empty>
 * </Combobox>
 * ```
 *
 * @example All options filtered out
 * ```tsx
 * <Combobox.List
 *   items={items}
 *   searchKeys={["name"]}
 *   noMatchesComponent={<Combobox.Empty>No matching items found</Combobox.Empty>}
 * >
 *   {(item) => <Combobox.ListItem key={item.id} value={item.name} />}
 * </Combobox.List>
 * ```
 */
const ComboboxEmpty: FC<ComboboxEmptyProps> = ({ children, className, ...props }) => {
    return (
        <Text className={cn("select-none px-3 py-2 text-base text-content-secondary", className)} {...props}>
            {children || "No options found"}
        </Text>
    );
};
ComboboxEmpty.displayName = COMBOBOX_EMPTY;

/**
 * Styled loading label for the combobox that can be used to display a message when the combobox is loading.
 * Usually passed as a child to the Combobox component.
 *
 * @example
 * ```tsx
 * <Combobox loading>
 *   <Combobox.Loading>Loading long list of fruits…</Combobox.Loading>
 * </Combobox>
 * ```
 */
const ComboboxLoading: FC<ComboboxLoadingProps> = ({ children, className, ...props }) => {
    return (
        <Text className={cn("select-none px-3 py-2 text-base text-content-secondary", className)} {...props}>
            {children || "Loading…"}
        </Text>
    );
};
ComboboxLoading.displayName = COMBOBOX_LOADING;

/**
 * Customizes how the selected value is displayed using either a render function or a react node.
 * It renders `children` in the dropdown trigger. You must wrap whatever you want to render in the trigger in this component.
 *
 * If rendered as is without any child, the selected value will be displayed using ValueLabel.
 *
 * @example With render function
 * ```tsx
 * <Combobox.Value>
 *   {(value) => (
 *     <div className="flex items-center gap-2">
 *       <IconStar className="size-4" />
 *       <Combobox.ValueLabel>{value}</Combobox.ValueLabel>
 *     </div>
 *   )}
 * </Combobox.Value>
 * ```
 *
 * @example With React node
 * ```tsx
 * <Combobox.Value>
 *   <div className="flex items-center gap-2">
 *     <IconStar className="size-4" />
 *     <span>Custom Value</span>
 *   </div>
 * </Combobox.Value>
 * ```
 */
const ComboboxValue: FC<
    Omit<React.ComponentProps<"span">, "children"> & {
        children?: React.ReactNode | ((value: string) => React.ReactNode);
    }
> = ({ children, ...props }) => {
    const store = useAriakitSelectContext();
    if (!store) {
        throw new Error("ComboboxValueLabel must be used within a Combobox component");
    }

    const value = store.useState("value");
    if (!children) {
        // if no children, render default styled label
        return <ComboboxValueLabel {...props}>{value}</ComboboxValueLabel>;
    }
    if (typeof children === "function") {
        // if child is a render function, call it with the value
        return children(value as string);
    }

    // child is a react node, simply return it
    return children;
};
ComboboxValue.displayName = COMBOBOX_VALUE;

/**
 * Default styled label for the selected value.
 * Usually rendered as a child of Value.
 */
const ComboboxValueLabel: FC<React.ComponentProps<"span">> = ({ children, className, ...props }) => {
    return (
        <span className={cn("truncate text-base text-content-primary", className)} {...props}>
            {children}
        </span>
    );
};
ComboboxValueLabel.displayName = "Combobox.ValueLabel";

const ComboboxFooter: FC<React.ComponentProps<"div">> = ({ children, className, ...props }) => {
    return (
        <div className={cn("border-t border-border-base px-3 py-2", className)} {...props}>
            {children}
        </div>
    );
};
ComboboxFooter.displayName = "Combobox.Footer";

Combobox.Popover = ComboboxPopover;
Combobox.List = ComboboxList;
Combobox.ListItem = ComboboxListItem;
Combobox.ListItemTitle = ComboboxListItemTitle;
Combobox.ListItemDescription = ComboboxListItemDescription;
Combobox.ListItemLeadingIcon = ComboboxListItemLeadingIcon;
Combobox.Empty = ComboboxEmpty;
Combobox.Value = ComboboxValue;
Combobox.ValueLabel = ComboboxValueLabel;
Combobox.Loading = ComboboxLoading;
Combobox.SearchBox = ComboboxSearchBox;
Combobox.Footer = ComboboxFooter;

export { Combobox };
